import { useId } from "react";

import type { InboxFilters as InboxFilterOptions, PersonRef } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { elementKindWord } from "../shared/words";
import type { FilterKey, InboxFilterValues } from "./useInboxFilters";

/** What the server filters an engineer by: the membership, else the person. */
export function engineerValue(person: PersonRef): string {
  return person.membership_id ?? person.person_id ?? person.display;
}

interface InboxFiltersProps {
  options: InboxFilterOptions;
  values: InboxFilterValues;
  onChange: (key: FilterKey, value: string) => void;
  onClear: () => void;
  active: boolean;
}

interface SelectProps {
  label: string;
  all: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}

function FilterSelect({ label, all, value, options, onChange }: SelectProps) {
  const id = useId();
  return (
    <div className="ra-filter">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      <select id={id} className="field__input" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{all}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Filters by kind of element, engineer, item and family. */
export function InboxFilters({ options, values, onChange, onClear, active }: InboxFiltersProps) {
  return (
    <form className="ra-filters" aria-label="Filter the inbox" onSubmit={(event) => event.preventDefault()}>
      <FilterSelect
        label="Kind of element"
        all="All kinds"
        value={values.kind ?? ""}
        options={options.kinds.map((kind) => ({ value: kind, label: elementKindWord(kind) }))}
        onChange={(value) => onChange("kind", value)}
      />
      <FilterSelect
        label="Engineer"
        all="All engineers"
        value={values.engineer ?? ""}
        options={options.engineers.map((person) => ({ value: engineerValue(person), label: person.display }))}
        onChange={(value) => onChange("engineer", value)}
      />
      <FilterSelect
        label="Item"
        all="All items"
        value={values.item ?? ""}
        options={options.items.map((item) => ({ value: item.id, label: item.name }))}
        onChange={(value) => onChange("item", value)}
      />
      <FilterSelect
        label="Family"
        all="All families"
        value={values.family ?? ""}
        options={options.families.map((family) => ({ value: family.id, label: family.name }))}
        onChange={(value) => onChange("family", value)}
      />
      {active && (
        <Button variant="quiet" onClick={onClear}>
          Clear the filters
        </Button>
      )}
    </form>
  );
}
